"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTOTP = void 0;
const react_1 = require("react");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const environment_1 = require("../utils/environment");
const useTOTP = () => {
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [setupResult, setSetupResult] = (0, react_1.useState)(null);
    const [verifyResult, setVerifyResult] = (0, react_1.useState)(null);
    const resetState = (0, react_1.useCallback)(() => {
        setError(null);
        setSetupResult(null);
        setVerifyResult(null);
    }, []);
    const setupTOTP = (0, react_1.useCallback)(async () => {
        setIsLoading(true);
        setError(null);
        setSetupResult(null);
        try {
            const session = await amplifyBootstrap_1.Auth.currentSession();
            const accessToken = session.tokens?.accessToken?.toString();
            if (!accessToken) {
                throw new Error('No access token available');
            }
            const response = await fetch(`${(0, environment_1.getApiGatewayUrl)()}/auth/totp/setup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const responseJson = await response.json();
            if (responseJson.error) {
                throw new Error(responseJson.error);
            }
            const result = {
                secretCode: responseJson.secretCode,
                qrCodeUrl: responseJson.qrCodeUrl,
                message: responseJson.message || 'TOTP setup successful',
            };
            setSetupResult(result);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const verifyTOTP = (0, react_1.useCallback)(async (totpCode) => {
        setIsLoading(true);
        setError(null);
        setVerifyResult(null);
        try {
            const session = await amplifyBootstrap_1.Auth.currentSession();
            const accessToken = session.tokens?.accessToken?.toString();
            if (!accessToken) {
                throw new Error('No access token available');
            }
            const response = await fetch(`${(0, environment_1.getApiGatewayUrl)()}/auth/totp/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ totpCode }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const responseJson = await response.json();
            if (responseJson.error) {
                throw new Error(responseJson.error);
            }
            const result = {
                status: responseJson.status,
                message: responseJson.message || 'TOTP verification successful',
                session: responseJson.session,
            };
            setVerifyResult(result);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const completeMfaChallenge = (0, react_1.useCallback)(async (challengeResponse) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.confirmSignIn({
                challengeResponse,
            });
            return result.isSignedIn;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            return false;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    return {
        isLoading,
        error,
        setupResult,
        verifyResult,
        setupTOTP,
        verifyTOTP,
        resetState,
        completeMfaChallenge,
    };
};
exports.useTOTP = useTOTP;
